import {type GetServerSidePropsContext} from 'next'
import {SkySlopeAddress} from '@skyslope/utils'

import {useRouter, useGetPublicListing, getPublicListingQuery, useCreateDraftOffer} from '~/hooks'
import {prefetchQueries} from '~/utils'
import {
  BuyerOfferFlowTypes,
  ChooseFlowPage,
  ThisListingIsNotReadyYetPage,
  useWriteOfferInForms,
} from '~/page-components'
import {getFeatureFlagsOnServer} from '~/server'

/**
 * Landing Page for TRREB (and other MLS's) that does a lookup to
 * see if a listing exists yet. If not, display a message with a button to
 * refresh. If the listing exists, give them the option of going to Forms
 * to create an offer or staying in Offers to upload one.
 */
export default function MLSSubmitOfferPage() {
  const {router, routes, query} = useRouter()

  const {mutateAsync: createDraftOffer} = useCreateDraftOffer()
  const {data: listing, isError: hadErrorGettingListing} = useGetPublicListing({
    market: query.market!,
    mlsListingId: query.mlsListing!,
  })
  const {writeOfferInFormsAsync} = useWriteOfferInForms()

  async function handleSubmit(flow: BuyerOfferFlowTypes) {
    if (flow === BuyerOfferFlowTypes.WriteOfferInForms) {
      await writeOfferInFormsAsync(listing)
      return
    }

    if (flow === BuyerOfferFlowTypes.UploadOffer) {
      const res = await createDraftOffer({fileId: listing!.fileId!})

      const propertyStub = SkySlopeAddress.formatShort(listing?.property)
        ?.replaceAll(' ', '-')
        .replaceAll(',', '')
        .toLowerCase()

      await router.push(
        routes.buyer.upload({
          linkId: listing?.linkId!,
          offerId: res.id,
          propertyStub: encodeURIComponent(propertyStub),
        }),
      )
      return
    }
  }

  return hadErrorGettingListing ? (
    <ThisListingIsNotReadyYetPage />
  ) : (
    <ChooseFlowPage
      listing={listing}
      onSubmit={handleSubmit}
      isUploadOfferEnabled={listing?.allowOfferSubmission ?? false}
    />
  )
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const {query} = context

  const market = query.market as string
  const mlsListingId = query.mlsListing as string

  const flags = await getFeatureFlagsOnServer()
  const queryResults = await prefetchQueries(getPublicListingQuery({market, mlsListingId}))
  return {props: {...queryResults, flags}}
}
